import React, { useEffect, useMemo, useState } from "react";
import {
  FiX,
  FiCheckCircle,
  FiCamera,
  FiFileText,
  FiImage,
  FiCalendar,
} from "react-icons/fi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBone,
  faBriefcaseMedical,
  faPersonWalking,
  faPaw,
} from "@fortawesome/free-solid-svg-icons";
import { httpService } from "../../services/httpService";
import config from "../../config";

// Import the S3 base URL from environment or use default
const S3_BASE_URL = (
  config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL ||
  "https://flps2025.s3.ap-south-1.amazonaws.com"
).replace(/\/+$/, "");

// ✅ Use the SAME URL resolution logic as FileUpload component
const resolvePreviewUrl = (preview) => {
  if (!preview) return "/icons/default-service.svg";
  if (preview.startsWith("blob:")) return preview;
  if (/^https?:\/\//i.test(preview)) return preview;
  return `${S3_BASE_URL}/${preview.replace(/^\/+/, "")}`;
};

// Activity type order for sorting
const ACTIVITY_TYPE_ORDER = {
  feeding: 1,
  walking: 2,
  medication: 3,
  custom: 4,
  activity: 5,
};

// Updated to merge Noon and Afternoon visually - REMOVED ICONS
const SLOT_CONFIG = [
  {
    key: "Morning",
    label: "Morning",
  },
  {
    key: "Afternoon", // Acts as the merged column for Noon & Afternoon
    label: "Afternoon",
  },
  {
    key: "Evening",
    label: "Evening",
  },
];

const getAllSlotKeys = (slots) => {
  if (!slots) return [];
  return Object.keys(slots);
};

// Fixed to merge Noon and Afternoon into the same slot
const getActivitiesForSlot = (slots, slotKey) => {
  if (!slots) return [];

  const targetKey = slotKey.toLowerCase();

  return Object.keys(slots)
    .filter((key) => {
      const currentKey = key.toLowerCase();
      // If the UI is asking for Afternoon, give it both afternoon AND noon activities
      if (
        targetKey === "afternoon" &&
        (currentKey === "afternoon" || currentKey === "noon")
      ) {
        return true;
      }
      return currentKey === targetKey;
    })
    .flatMap((key) => slots[key] || []);
};

// Sort activities by type order
const sortActivities = (activities) => {
  return [...activities].sort((a, b) => {
    const typeA = a?.scheduled_task_type?.toLowerCase() || "custom";
    const typeB = b?.scheduled_task_type?.toLowerCase() || "custom";
    const orderA = ACTIVITY_TYPE_ORDER[typeA] || 99;
    const orderB = ACTIVITY_TYPE_ORDER[typeB] || 99;
    return orderA - orderB;
  });
};

// Safe date formatting to prevent timezone shifts on YYYY-MM-DD strings
const formatDate = (dateString) => {
  if (!dateString) return "";

  if (
    typeof dateString === "string" &&
    dateString.includes("-") &&
    !dateString.includes("T")
  ) {
    const [year, month, day] = dateString.split("-");
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }

  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatDay = (dateString) => {
  if (!dateString) return "";

  if (
    typeof dateString === "string" &&
    dateString.includes("-") &&
    !dateString.includes("T")
  ) {
    const [year, month, day] = dateString.split("-");
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
    });
  }

  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });
};

const getActivityLabel = (activity) => {
  const type = activity?.scheduled_task_type?.toLowerCase();

  if (type === "feeding") {
    return activity?.raw_data?.whatToFeed || activity.description || "Feeding";
  }

  if (type === "walking" || type === "walk") {
    if (activity?.raw_data) {
      const duration = activity.raw_data.duration
        ? `${activity.raw_data.duration} min`
        : "";

      const notes = activity.raw_data.notes || "";

      return (
        [duration, notes].filter(Boolean).join(", ") ||
        activity.description ||
        "Walk"
      );
    }

    return activity.description || "Walk";
  }

  if (type === "medication") {
    return activity.description || "Medication";
  }

  return activity.description || "Custom activity";
};

const getActivityIcon = (activity) => {
  const type = activity?.scheduled_task_type?.toLowerCase();

  if (type === "feeding") {
    return (
      <FontAwesomeIcon icon={faBone} className="text-orange-500 shrink-0" />
    );
  }

  if (type === "walking" || type === "walk") {
    return (
      <FontAwesomeIcon
        icon={faPersonWalking}
        className="text-blue-500 shrink-0"
      />
    );
  }

  if (type === "medication") {
    return (
      <FontAwesomeIcon
        icon={faBriefcaseMedical}
        className="text-red-500 shrink-0"
      />
    );
  }

  return <FontAwesomeIcon icon={faPaw} className="text-indigo-500 shrink-0" />;
};

// FIXED: Always return the activity type, not the medicine name
const getActivityTypeName = (activity) => {
  const type = activity?.scheduled_task_type?.toLowerCase();

  switch (type) {
    case "feeding":
      return "Feeding";
    case "walking":
    case "walk":
      return "Walk";
    case "medication":
      return "Medication"; // Always return "Medication", not the medicine name
    default:
      return activity?.description || "Custom Activity";
  }
};

// For displaying the activity details (medicine name, what to feed, etc.)
const getActivityDetails = (activity) => {
  const type = activity?.scheduled_task_type?.toLowerCase();

  if (type === "feeding") {
    return activity?.raw_data?.whatToFeed || "";
  }

  if (type === "walking" || type === "walk") {
    if (activity?.raw_data) {
      const duration = activity.raw_data.duration
        ? `${activity.raw_data.duration} min`
        : "";
      const notes = activity.raw_data.notes || "";
      return [duration, notes].filter(Boolean).join(", ");
    }
    return "";
  }

  if (type === "medication") {
    // For medication, show the medicine name as details
    return (
      activity?.raw_data?.medicine_name ||
      activity?.raw_data?.medicineName ||
      activity?.raw_data?.name ||
      ""
    );
  }

  return "";
};

const BoardingActivitySummaryDrawer = ({
  isOpen,
  onClose,
  serviceRefId,
  petName,
  serviceDetails,
}) => {
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("schedule");
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    if (!isOpen || !serviceRefId) return;

    const fetchActivities = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await httpService.get(
          `/serviceactivity/boarding/${serviceRefId}/activities`,
        );

        const scheduleData =
          response?.data?.data?.schedule ||
          response?.data?.schedule ||
          response?.schedule ||
          [];
        setSchedule(scheduleData);
      } catch (err) {
        console.error("Failed to fetch boarding activities:", err);
        setError("Unable to load boarding activity summary.");
        setSchedule([]);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [isOpen, serviceRefId]);

  const summary = useMemo(() => {
    // Extract all activities and attach the date/slot to them for the notes/photos tabs
    const allActivities = schedule.flatMap((day) => {
      const slots = day?.slots || {};
      const slotKeys = getAllSlotKeys(slots);

      return slotKeys.flatMap((slotKey) => {
        const activitiesInSlot = slots[slotKey] || [];
        return activitiesInSlot.map((act) => ({
          ...act,
          displayDate: day.date,
          displaySlot: slotKey,
        }));
      });
    });

    const completed = allActivities.filter(
      (a) => a?.is_completed === true,
    ).length;

    const photosCount = allActivities.reduce(
      (total, a) => total + (a?.photos?.length || 0),
      0,
    );

    const medications = allActivities.filter(
      (a) => a?.scheduled_task_type?.toLowerCase() === "medication",
    );
    const completedMedications = medications.filter(
      (a) => a?.is_completed === true,
    ).length;

    // Build the lists for the Notes and Photos tabs
    const notesList = allActivities
      .filter((a) => a.notes && a.notes.trim() !== "")
      .sort((a, b) => {
        // Sort by date first
        const dateA = new Date(a.displayDate);
        const dateB = new Date(b.displayDate);
        if (dateA - dateB !== 0) return dateA - dateB;

        // Then by activity type
        const typeA = a?.scheduled_task_type?.toLowerCase() || "custom";
        const typeB = b?.scheduled_task_type?.toLowerCase() || "custom";
        const orderA = ACTIVITY_TYPE_ORDER[typeA] || 99;
        const orderB = ACTIVITY_TYPE_ORDER[typeB] || 99;
        return orderA - orderB;
      });

    const photosList = allActivities.flatMap((a) => {
      if (!a.photos || !Array.isArray(a.photos)) return [];
      return a.photos.map((url) => ({
        url,
        date: a.displayDate,
        slot: a.displaySlot,
        activity: a,
        activityTitle: getActivityTypeName(a),
        activityDetails: getActivityDetails(a),
        notes: a.notes,
      }));
    });

    return {
      total: allActivities.length,
      completed,
      photos: photosCount,
      medications: medications.length,
      completedMedications,
      notesList,
      photosList,
    };
  }, [schedule]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-6xl bg-slate-50 shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
                  Boarding Summary
                </p>

                <h2 className="text-xl font-bold text-slate-900">
                  {petName || "Pet"}
                </h2>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
                  {serviceDetails?.startDate && (
                    <span>
                      📅 {formatDate(serviceDetails.startDate)}
                      {serviceDetails?.endDate &&
                        ` – ${formatDate(serviceDetails.endDate)}`}
                    </span>
                  )}

                  {serviceDetails?.orderNumber && (
                    <span className="bg-slate-100 px-2 py-0.5 rounded-md text-xs font-medium">
                      Order #{serviceDetails.orderNumber}
                    </span>
                  )}

                  {serviceDetails?.roomName &&
                    serviceDetails.roomName !== "N/A" && (
                      <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-md text-xs font-medium">
                        {serviceDetails.roomName}
                        {serviceDetails.roomNumber
                          ? ` • ${serviceDetails.roomNumber}`
                          : ""}
                      </span>
                    )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {/* Share button */}
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors"
              >
                Share
              </button>

              {/* Close button */}
              <button
                type="button"
                onClick={onClose}
                className="w-9 h-9 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 md:p-6">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="text-sm text-slate-500 flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                Loading boarding activity summary...
              </div>
            </div>
          )}

          {!loading && error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
                {/* Activities Logged */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Activities Logged
                  </p>

                  <p className="text-3xl font-bold text-slate-900 mt-3">
                    {summary.completed} / {summary.total}
                  </p>

                  <p className="text-sm text-slate-500 mt-1">
                    Across {schedule.length} days, {SLOT_CONFIG.length}{" "}
                    shifts/day
                  </p>
                </div>

                {/* Completion Rate */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Completion Rate
                  </p>

                  <p className="text-3xl font-bold text-slate-900 mt-3">
                    {summary.total > 0
                      ? Math.round((summary.completed / summary.total) * 100)
                      : 0}
                    %
                  </p>

                  <p className="text-sm text-slate-500 mt-1">
                    {summary.total > 0 && summary.completed === summary.total
                      ? "All scheduled activities done"
                      : `${summary.total - summary.completed} activities remaining`}
                  </p>
                </div>

                {/* Photos Captured - ONLY SHOW WHEN PHOTOS EXIST */}
                {summary.photos > 0 && (
                  <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Photos Captured
                    </p>

                    <p className="text-3xl font-bold text-slate-900 mt-3">
                      {summary.photos}
                    </p>

                    <p className="text-sm text-slate-500 mt-1">
                      Attached across feeding & walks
                    </p>
                  </div>
                )}

                {/* Medication Given - ONLY SHOW WHEN MEDICATION EXISTS */}
                {summary.medications > 0 && (
                  <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Medication Given
                    </p>

                    <p className="text-3xl font-bold text-slate-900 mt-3">
                      {summary.completedMedications} / {summary.medications}
                    </p>

                    <p className="text-sm text-slate-500 mt-1">
                      Medication recorded during stay
                    </p>
                  </div>
                )}
              </div>

              {/* Tabs */}
              <div className="flex space-x-1 bg-slate-200/50 p-1 rounded-xl w-max mb-6">
                {/* Daily Schedule - Always show */}
                <button
                  onClick={() => setActiveTab("schedule")}
                  className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg transition-all ${
                    activeTab === "schedule"
                      ? "bg-white text-indigo-700 shadow-sm"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                  }`}
                >
                  <FiCalendar className="w-4 h-4" />
                  Daily Schedule
                </button>

                {/* Notes - Show only when notes exist */}
                {summary.notesList.length > 0 && (
                  <button
                    onClick={() => setActiveTab("notes")}
                    className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg transition-all ${
                      activeTab === "notes"
                        ? "bg-white text-indigo-700 shadow-sm"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                    }`}
                  >
                    <FiFileText className="w-4 h-4" />
                    Notes
                    <span className="ml-1.5 bg-indigo-100 text-indigo-700 py-0.5 px-2 rounded-full text-xs">
                      {summary.notesList.length}
                    </span>
                  </button>
                )}

                {/* Photos - Show only when photos exist */}
                {summary.photosList.length > 0 && (
                  <button
                    onClick={() => setActiveTab("photos")}
                    className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg transition-all ${
                      activeTab === "photos"
                        ? "bg-white text-indigo-700 shadow-sm"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                    }`}
                  >
                    <FiImage className="w-4 h-4" />
                    Photos
                    <span className="ml-1.5 bg-indigo-100 text-indigo-700 py-0.5 px-2 rounded-full text-xs">
                      {summary.photosList.length}
                    </span>
                  </button>
                )}
              </div>

              {/* TAB CONTENT: Schedule -*/}
              {activeTab === "schedule" && (
                <div className="animate-in fade-in duration-300">
                  <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                      <div
                        className="grid border-b border-slate-200 bg-slate-50 min-w-[600px]"
                        style={{
                          gridTemplateColumns: `140px repeat(${SLOT_CONFIG.length}, 1fr)`,
                        }}
                      >
                        <div className="px-4 py-4 text-sm font-semibold text-slate-600 border-r border-slate-200">
                          Date
                        </div>

                        {SLOT_CONFIG.map((slot) => (
                          <div
                            key={slot.key}
                            className="px-4 py-4 text-sm font-semibold text-slate-700 border-r last:border-r-0 border-slate-200 text-center"
                          >
                            {slot.label}
                          </div>
                        ))}
                      </div>

                      {schedule.length === 0 ? (
                        <div className="px-5 py-12 text-center text-slate-500 bg-slate-50/50">
                          No activities scheduled for this boarding stay.
                        </div>
                      ) : (
                        schedule.map((day) => (
                          <div
                            key={day.date}
                            className="grid border-b last:border-b-0 border-slate-200 min-w-[600px] hover:bg-slate-50/30 transition-colors"
                            style={{
                              gridTemplateColumns: `140px repeat(${SLOT_CONFIG.length}, 1fr)`,
                            }}
                          >
                            <div className="px-4 py-5 text-sm font-bold text-slate-900 whitespace-nowrap border-r border-slate-200 bg-slate-50/50">
                              {formatDay(day.date)}
                            </div>

                            {SLOT_CONFIG.map((slot) => {
                              const activities = sortActivities(
                                getActivitiesForSlot(
                                  day?.slots || {},
                                  slot.key,
                                ),
                              );

                              return (
                                <div
                                  key={slot.key}
                                  className="px-4 py-5 min-w-0 border-r last:border-r-0 border-slate-200"
                                >
                                  {activities.length === 0 ? (
                                    <span className="text-sm text-slate-300">
                                      —
                                    </span>
                                  ) : (
                                    <div className="space-y-3">
                                      {activities.map((activity) => {
                                        const completed =
                                          activity?.is_completed === true;
                                        const label =
                                          getActivityLabel(activity);

                                        return (
                                          <div
                                            key={activity._id}
                                            className="flex items-center gap-2"
                                          >
                                            {/* Checkbox */}
                                            <div className="flex items-center justify-center w-5 h-5 shrink-0">
                                              {completed ? (
                                                <FiCheckCircle className="w-4 h-4 text-green-700" />
                                              ) : (
                                                <span className="w-5 h-5 rounded-full border-2 border-slate-300" />
                                              )}
                                            </div>

                                            {/* Icon only - removed activity name */}
                                            <div className="flex items-center justify-center w-5 h-5 shrink-0">
                                              {getActivityIcon(activity)}
                                            </div>

                                            {/* Activity label (details like "1 Cup Chicken", "15 min", etc.) */}
                                            <div className="min-w-0 flex-1">
                                              <span className="text-sm text-slate-600">
                                                {label}
                                              </span>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB CONTENT: Notes - Fixed to show "Medication" instead of medicine name */}
              {activeTab === "notes" && (
                <div className="animate-in fade-in duration-300">
                  {summary.notesList.length === 0 ? (
                    <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500">
                      <FiFileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p>No notes have been logged for this boarding stay.</p>
                    </div>
                  ) : (
                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                      <div className="overflow-x-auto">
                        <table className="w-full table-fixed">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-[140px]">
                                Date
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-[140px]">
                                Activity
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-[140px]">
                                Slot
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                                Note
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {summary.notesList.map((activity, idx) => {
                              const prevActivity =
                                idx > 0 ? summary.notesList[idx - 1] : null;
                              const showDate =
                                !prevActivity ||
                                prevActivity.displayDate !==
                                  activity.displayDate;

                              // Capitalize the first letter of slot
                              const capitalizedSlot = activity.displaySlot
                                ? activity.displaySlot.charAt(0).toUpperCase() +
                                  activity.displaySlot.slice(1).toLowerCase()
                                : "";

                              return (
                                <tr
                                  key={idx}
                                  className="hover:bg-slate-50/50 transition-colors"
                                >
                                  <td className="px-4 py-3 text-sm font-medium text-slate-700 whitespace-nowrap w-[120px]">
                                    {showDate
                                      ? formatDay(activity.displayDate)
                                      : ""}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-slate-700 w-[150px]">
                                    <div className="flex items-center gap-2">
                                      {getActivityIcon(activity)}
                                      <span className="truncate">
                                        {getActivityTypeName(activity)}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-slate-600 w-[100px]">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                                      {capitalizedSlot}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-slate-700">
                                    <div className="whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                                      {activity.notes}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TAB CONTENT: Photos */}
              {activeTab === "photos" && (
                <div className="animate-in fade-in duration-300">
                  {summary.photosList.length === 0 ? (
                    <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500">
                      <FiCamera className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p>
                        No photos have been uploaded for this boarding stay.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {summary.photosList.map((photo, idx) => {
                          const imageUrl = resolvePreviewUrl(photo.url);

                          return (
                            <div
                              key={idx}
                              className="relative group rounded-xl overflow-hidden border border-slate-200 aspect-square bg-slate-100 cursor-pointer hover:shadow-lg transition-shadow"
                              onClick={() => setSelectedPhoto(photo)}
                            >
                              <img
                                src={imageUrl}
                                alt={photo.activityTitle || "Boarding Activity"}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                onError={(e) => {
                                  console.error(
                                    "Failed to load image:",
                                    imageUrl,
                                  );
                                  e.target.onerror = null;
                                  e.target.src = "/icons/default-service.svg";
                                }}
                              />
                              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent p-3 pt-12 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                                <p className="text-white text-xs font-bold leading-tight line-clamp-1">
                                  {photo.activityTitle}
                                </p>
                                {photo.activityDetails && (
                                  <p className="text-slate-300 text-[10px] mt-0.5">
                                    {photo.activityDetails}
                                  </p>
                                )}
                                <p className="text-slate-300 text-[10px] mt-0.5">
                                  {formatDay(photo.date)} •{" "}
                                  {photo.slot
                                    ? photo.slot.charAt(0).toUpperCase() +
                                      photo.slot.slice(1)
                                    : ""}
                                </p>
                                {photo.notes && (
                                  <p className="text-slate-300 text-[10px] mt-0.5 line-clamp-1">
                                    📝 {photo.notes}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Photo Modal */}
                      {selectedPhoto && (
                        <div
                          className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4"
                          onClick={() => setSelectedPhoto(null)}
                        >
                          <div className="relative max-w-4xl w-full max-h-[90vh] bg-white rounded-2xl overflow-hidden">
                            <button
                              onClick={() => setSelectedPhoto(null)}
                              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors flex items-center justify-center"
                            >
                              <FiX className="w-6 h-6" />
                            </button>
                            <img
                              src={resolvePreviewUrl(selectedPhoto.url)}
                              alt={selectedPhoto.activityTitle}
                              className="w-full h-auto max-h-[70vh] object-contain"
                              onError={(e) => {
                                console.error(
                                  "Failed to load modal image:",
                                  resolvePreviewUrl(selectedPhoto.url),
                                );
                                e.target.onerror = null;
                                e.target.src = "/icons/default-service.svg";
                              }}
                            />
                            <div className="p-4 bg-white border-t border-slate-200">
                              <p className="font-semibold text-slate-900">
                                {selectedPhoto.activityTitle}
                              </p>
                              {selectedPhoto.activityDetails && (
                                <p className="text-sm text-slate-600">
                                  {selectedPhoto.activityDetails}
                                </p>
                              )}
                              <p className="text-sm text-slate-600">
                                {formatDate(selectedPhoto.date)} •{" "}
                                {selectedPhoto.slot
                                  ? selectedPhoto.slot.charAt(0).toUpperCase() +
                                    selectedPhoto.slot.slice(1)
                                  : ""}
                              </p>
                              {selectedPhoto.notes && (
                                <p className="text-sm text-slate-700 mt-2">
                                  {selectedPhoto.notes}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BoardingActivitySummaryDrawer;
